'use client';
import { use, useCallback, useEffect, useMemo } from 'react';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { Button } from '@heroui/button';
import { useRouter } from 'next/navigation';

import { BackButton } from '@/components/icons/BackButton';

const PDF_DOC_PATH: { [key: string]: string } = {
  tos: '/TOS.pdf',
  privacy: '/PrivacyPolicy.pdf',
};

export default function DocPage(props: { params: Promise<{ pdf: string }> }) {
  const params = use(props.params);
  const pdfpath = PDF_DOC_PATH[params.pdf];
  const router = useRouter();
  const isIOSNative =
    Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
  const pdfUrl = useMemo(() => {
    if (!pdfpath) {
      return undefined;
    }

    if (typeof window === 'undefined') {
      return pdfpath;
    }

    return new URL(pdfpath, window.location.origin).toString();
  }, [pdfpath]);

  function handleBackButtonPressed() {
    router.back();
  }

  const openInNativeViewer = useCallback(async () => {
    if (!pdfUrl) {
      return;
    }

    await Browser.open({
      url: pdfUrl,
      presentationStyle: 'fullscreen',
    });
  }, [pdfUrl]);

  useEffect(() => {
    if (!isIOSNative || !pdfUrl) {
      return;
    }

    openInNativeViewer().catch(() => {
      // If Browser plugin fails, user can still use the manual fallback button.
    });
  }, [isIOSNative, pdfUrl, openInNativeViewer]);

  if (!pdfpath) {
    return (
      <div className="flex flex-col items-center justify-center w-full max-w-[600px] h-[700px] gap-4">
        <Button
          isIconOnly
          aria-label="Back"
          className="bg-background self-start"
          onPress={handleBackButtonPressed}
        >
          <BackButton height={20} />
        </Button>
        <p className="font-maisonneue text-[#565656]">Document not found.</p>
      </div>
    );
  }

  if (isIOSNative) {
    return (
      <div className="flex flex-col items-center justify-start w-full max-w-[600px] h-[700px] px-4">
        <div className="pl-[10px] pb-[10px] w-full">
          <Button
            isIconOnly
            aria-label="Back"
            className="bg-background"
            onPress={handleBackButtonPressed}
          >
            <BackButton height={20} />
          </Button>
        </div>

        <div className="w-full max-w-[420px] mt-8 rounded-[24px] border border-[#ECECEC] shadow-[0_0_16px_0_rgba(0,0,0,0.08)] bg-white p-6 flex flex-col gap-4 text-center">
          <h1 className="font-maisonneue font-semibold text-[24px] text-black">
            Open Document
          </h1>
          <p className="font-maisonneue text-[15px] text-[#565656]">
            For best readability on iOS, this document opens in the native PDF
            viewer.
          </p>
          <Button
            className="h-12 bg-linear-to-r from-[#CFE7FF] via-[#D6E9FC] to-[#E9EFF7] text-[#1E1E1E] font-maisonneue font-semibold rounded-[24px]"
            onPress={() => {
              openInNativeViewer().catch(() => {
                // Keep silent; user still has the direct link fallback below.
              });
            }}
          >
            Open PDF
          </Button>
          <a
            className="font-maisonneue text-[14px] text-[#2F4A6B] underline"
            href={pdfUrl}
            rel="noreferrer"
            target="_blank"
          >
            Open in new tab
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center w-full md:w-[600px] h-[700px]">
      <div className="pl-[10px] pb-[10px] w-full">
        <Button
          isIconOnly
          aria-label="Back"
          className="bg-background"
          onPress={handleBackButtonPressed}
        >
          <BackButton height={20} />
        </Button>
      </div>
      <iframe className="w-full h-full" src={pdfpath} title={params.pdf} />
    </div>
  );
}
